import { Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { Check, MapPin, Quote, ChevronDown, ChevronRight } from "lucide-react";
import { Reveal, CallButton, TextButton, GBP } from "@/components/site-chrome";
import { PHOTOS, steps, why, audiences, towns, type Faq, type PhotoName, type Review } from "@/lib/site-data";
import { SERVICES, type Service } from "@/lib/services-data";

/* ---------- Photos ---------- */

function imageSrc(name: string, width: number, ext: "avif" | "webp") {
  return `/images/${name}-${width}.${ext}`;
}
export function imageSrcSet(name: string, widths: readonly number[], ext: "avif" | "webp") {
  return widths.map((width) => `${imageSrc(name, width, ext)} ${width}w`).join(", ");
}
export { imageSrc };

type PhotoSource = { media?: string; widths: readonly number[]; sizes: string };

export function Photo({
  name, widths, sizes, sources, alt, width, height, className, pictureClassName, loading, fetchPriority,
}: {
  name: string;
  widths?: readonly number[];
  sizes?: string;
  sources?: readonly PhotoSource[];
  alt: string;
  width: number;
  height: number;
  className: string;
  pictureClassName?: string;
  loading?: "lazy";
  fetchPriority?: "high";
}) {
  const groups: readonly PhotoSource[] = sources ?? [{ widths: widths!, sizes: sizes! }];
  const fallback = groups[groups.length - 1]!;
  const fallbackWidths = [...new Set(groups.flatMap((group) => group.widths))].sort((a, b) => a - b);
  return (
    <picture className={pictureClassName}>
      {groups.map((group) => (
        <source key={`avif-${group.media ?? "all"}`} {...(group.media ? { media: group.media } : {})} type="image/avif" srcSet={imageSrcSet(name, group.widths, "avif")} sizes={group.sizes} />
      ))}
      {groups.map((group) => (
        <source key={`webp-${group.media ?? "all"}`} {...(group.media ? { media: group.media } : {})} type="image/webp" srcSet={imageSrcSet(name, group.widths, "webp")} sizes={group.sizes} />
      ))}
      <img
        src={imageSrc(name, fallback.widths[fallback.widths.length - 1]!, "webp")}
        srcSet={imageSrcSet(name, fallbackWidths, "webp")}
        sizes={fallback.sizes}
        alt={alt}
        width={width}
        height={height}
        className={className}
        {...(loading ? { loading } : {})}
        {...(fetchPriority ? { fetchPriority } : {})}
      />
    </picture>
  );
}

export function SitePhoto({ name, alt, className, sizes, lazy = true }: { name: PhotoName; alt?: string; className: string; sizes: string; lazy?: boolean }) {
  const p = PHOTOS[name];
  return (
    <Photo name={name} widths={p.widths} sizes={sizes} alt={alt ?? p.alt} width={p.width} height={p.height} pictureClassName="block w-full" className={className} {...(lazy ? { loading: "lazy" as const } : {})} />
  );
}

/* ---------- Small pieces ---------- */

export function Eyebrow({ children }: { children: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="rule-orange" aria-hidden />
      <p className="label-mono text-primary">{children}</p>
    </div>
  );
}

export function Breadcrumb({ items }: { items: { label: string; to?: "/" | "/services" }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="label-mono text-steel">
      <ol className="flex flex-wrap items-center gap-1">
        {items.map((it, i) => (
          <li key={it.label} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3 w-3" aria-hidden />}
            {it.to ? <Link to={it.to} className="hover:text-primary">{it.label}</Link> : <span aria-current="page" className="text-foreground/80">{it.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** Subpage hero: dark photo background, breadcrumb, the page's single H1, Call/Text. */
export function PageHero({ eyebrow, title, intro, photo, crumbs, children }: {
  eyebrow?: string;
  title: ReactNode;
  intro: string;
  photo: PhotoName;
  crumbs?: { label: string; to?: "/" | "/services" }[];
  children?: ReactNode;
}) {
  const p = PHOTOS[photo];
  return (
    <section className="relative cut-bottom overflow-hidden bg-charcoal-deep">
      <Photo name={photo} widths={p.widths} sizes="100vw" alt={p.alt} width={p.width} height={p.height} fetchPriority="high" pictureClassName="absolute inset-0" className="h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal-deep via-charcoal-deep/85 to-charcoal-deep/30" />
      <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-10 sm:px-6 md:pb-32 md:pt-16">
        {crumbs && <Breadcrumb items={crumbs} />}
        {eyebrow && <p className="label-mono mt-6 text-steel">{eyebrow}</p>}
        <h1 className="mt-4 max-w-4xl text-5xl font-black sm:text-6xl md:text-7xl">{title}</h1>
        <p className="mt-6 max-w-2xl text-lg text-foreground/90">{intro}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <CallButton label="Call Now" size="lg" />
          <TextButton label="Text Josh" size="lg" />
        </div>
        {children}
      </div>
    </section>
  );
}

export function WeatherStrip() {
  const items = ["Rain", "Sleet", "Snow", "Sunshine", "We get the job done"];
  const row = [...items, ...items, ...items];
  return (
    <div className="overflow-hidden border-y-2 border-primary bg-charcoal-deep py-3" role="img" aria-label="Rain, sleet, snow, or sunshine — we get the job done">
      <div className="marquee flex w-max gap-8 whitespace-nowrap font-display text-2xl font-extrabold uppercase tracking-widest text-primary" aria-hidden="true">
        {[...row, ...row].map((w, i) => (
          <span key={i} className={i % 5 === 4 ? "text-foreground" : ""}>{w} <span className="text-steel">·</span></span>
        ))}
      </div>
    </div>
  );
}

/* ---------- Services ---------- */

export function ServiceCard({ s }: { s: Service }) {
  return (
    <Reveal>
      <Link to="/services/$slug" params={{ slug: s.slug }} className="group flex h-full flex-col rounded-sm border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-primary">
        <s.icon className="h-7 w-7 text-primary" aria-hidden />
        <h3 className="mt-4 text-2xl font-extrabold">{s.name}</h3>
        <p className="mt-2 flex-1 text-sm text-muted-foreground">{s.short}</p>
        <span className="label-mono mt-4 text-steel group-hover:text-primary">Learn more →</span>
      </Link>
    </Reveal>
  );
}

/** Lead "Mobile Auto Repair" card plus the other 11 services. */
export function ServiceGrid() {
  const lead = SERVICES[0]!;
  const rest = SERVICES.slice(1);
  return (
    <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Reveal className="sm:col-span-2 lg:row-span-2">
        <Link to="/services/$slug" params={{ slug: lead.slug }} className="group flex h-full flex-col justify-between rounded-sm border-2 border-primary bg-card p-8 transition-transform hover:-translate-y-1">
          <div>
            <lead.icon className="h-12 w-12 text-primary" aria-hidden />
            <p className="label-mono mt-6 text-primary">Lead service</p>
            <h3 className="mt-2 text-5xl font-black">{lead.name}</h3>
            <p className="mt-4 max-w-md text-foreground/85">{lead.short}</p>
          </div>
          <span className="label-mono mt-8 text-primary group-hover:underline">Learn more →</span>
        </Link>
      </Reveal>
      {rest.map((s) => <ServiceCard key={s.slug} s={s} />)}
    </div>
  );
}

/* ---------- How it works ---------- */

export function HowItWorks({ heading = "How Our Mobile Mechanic Service Works", className = "bg-background py-24" }: { heading?: string; className?: string }) {
  return (
    <section className={className} aria-labelledby="how-h">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Eyebrow>Dispatch board</Eyebrow>
        <h2 id="how-h" className="mt-4 text-5xl font-black md:text-6xl">{heading}</h2>
        <ol className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((s) => (
            <Reveal as="li" key={s.n} className="cut-corner border-2 border-dashed border-steel/50 bg-card p-6">
              <div className="label-mono flex justify-between border-b border-border pb-3 text-steel">
                <span>Job ticket</span><span>#406-{s.n}</span>
              </div>
              <p className="mt-4 font-display text-6xl font-black text-primary">{s.n}</p>
              <h3 className="mt-2 text-2xl font-extrabold">{s.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              <div className="hazard mt-6 h-2 rounded-sm" aria-hidden />
            </Reveal>
          ))}
        </ol>
        <p className="mt-10 border-l-2 border-primary pl-4 font-display text-2xl font-bold uppercase md:text-3xl">Communication is key — you'll always know what's happening.</p>
      </div>
    </section>
  );
}

/* ---------- Problem story ---------- */

export function ProblemStory() {
  return (
    <section className="bg-background py-24" aria-labelledby="problem-h">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:items-center">
        <Reveal>
          <Eyebrow>Sound familiar?</Eyebrow>
          <h2 id="problem-h" className="mt-4 text-5xl font-black md:text-6xl">Car Trouble Never Picks a Good Time</h2>
          <p className="mt-6 text-lg text-foreground/85">
            Broken down on the way to work. Car won't start in the cold. No time to sit in a shop waiting room — and maybe you've been overcharged or burned by a mechanic before.
          </p>
          <p className="mt-4 text-lg text-foreground/85">
            Josh does it differently. He shows up when he says he will, shows you the problem firsthand, and fixes it right the first time. No runaround, no surprises.
          </p>
          <div className="mt-8 flex flex-wrap gap-3"><CallButton /><TextButton label="Text Josh" /></div>
        </Reveal>
        <Reveal>
          <Photo
            name="truck"
            sources={[
              { media: "(max-width: 639px)", widths: [480], sizes: "calc(100vw - 2rem)" },
              { media: "(max-width: 1023px)", widths: [640, 960], sizes: "calc(100vw - 3rem)" },
              { widths: [640, 960], sizes: "calc((min(100vw, 80rem) - 6rem) / 2.1)" },
            ]}
            alt={PHOTOS.truck.alt}
            width={1200}
            height={900}
            loading="lazy"
            pictureClassName="block w-full"
            className="cut-corner aspect-[4/3] w-full object-cover"
          />
          <p className="label-mono mt-3 text-steel">Fully equipped mobile shop · Ready for Montana roads</p>
        </Reveal>
      </div>
    </section>
  );
}

export function RealWork() {
  const items: { name: PhotoName; cap: string }[] = [
    { name: "lettering", cap: "The rig you'll see at your door" },
    { name: "towing", cap: "Towing & transport when a job needs it" },
    { name: "ac", cap: "AC verified cold before we call it done" },
  ];
  return (
    <section className="bg-background pb-24" aria-labelledby="onthejob-h">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Eyebrow>On the job</Eyebrow>
        <h2 id="onthejob-h" className="mt-4 text-4xl font-black md:text-5xl">Real Work, Around Great Falls</h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {items.map((it) => (
            <Reveal key={it.name}>
              <SitePhoto name={it.name} sizes="(min-width: 640px) 33vw, 100vw" className="cut-corner aspect-[4/5] w-full object-cover" />
              <p className="label-mono mt-3 text-steel">{it.cap}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Why / Who ---------- */

export function WhyChoose() {
  return (
    <section className="bg-paper py-24 text-paper-foreground" aria-labelledby="why-h">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.2fr]">
        <Reveal>
          <Eyebrow>The difference</Eyebrow>
          <h2 id="why-h" className="mt-4 text-5xl font-black md:text-6xl">Why Choose 406 Mobile Mechanic</h2>
          <p className="mt-5 text-lg">Owner-operated by Josh Ginnaty. When you call, you get the mechanic who actually does the work.</p>
        </Reveal>
        <ul className="grid gap-px overflow-hidden rounded-sm bg-paper-foreground/15 sm:grid-cols-2">
          {why.map((w) => (
            <Reveal as="li" key={w} className="flex items-start gap-3 bg-paper p-5 font-semibold">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />{w}
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function WhoWeServe({ fleetCallout = true }: { fleetCallout?: boolean }) {
  return (
    <section className="topo cut-top bg-charcoal-deep pb-24 pt-32" aria-labelledby="who-h">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Eyebrow>Who we serve</Eyebrow>
        <h2 id="who-h" className="mt-4 text-5xl font-black md:text-6xl">Mobile Auto Repair for Every Driver</h2>
        <ul className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-5">
          {audiences.map((a) => (
            <Reveal as="li" key={a.t} className="rounded-sm border border-border bg-card p-5 text-center">
              <a.icon className="mx-auto h-8 w-8 text-primary" aria-hidden />
              <p className="mt-3 font-display text-xl font-bold uppercase">{a.t}</p>
              {a.note && <p className="label-mono mt-1 text-primary">{a.note}</p>}
            </Reveal>
          ))}
        </ul>
        {fleetCallout && (
          <Reveal className="mt-8 flex flex-wrap items-center justify-between gap-6 rounded-sm border-l-4 border-primary bg-card p-6 md:p-8">
            <div>
              <p className="label-mono text-primary">Fleet vehicle maintenance · Montana</p>
              <p className="mt-2 font-display text-3xl font-black uppercase md:text-4xl">Keep your business vehicles on the road — fleet service available.</p>
            </div>
            <Link to="/fleet-service" className="inline-flex items-center gap-2 rounded-sm bg-primary px-4 py-2 font-display text-base font-extrabold uppercase tracking-wide text-primary-foreground transition-transform hover:-translate-y-0.5">
              Talk Fleet Service →
            </Link>
          </Reveal>
        )}
      </div>
    </section>
  );
}

/* ---------- Reviews ---------- */

export function ReviewCard({ r, featured = false }: { r: Review; featured?: boolean }) {
  const [open, setOpen] = useState(false);
  const clamp = featured && r.text.length > 300;
  return (
    <Reveal as="article" className={`flex flex-col overflow-hidden rounded-sm border border-border bg-card ${featured ? "p-8 md:col-span-2 md:p-10" : "p-8"}`}>
      <Quote className={featured ? "h-14 w-14 text-primary" : "h-10 w-10 text-primary"} aria-hidden />
      <blockquote className={`mt-4 flex-1 leading-relaxed ${featured ? "text-lg md:text-xl" : "text-lg"} ${clamp && !open ? "line-clamp-5 md:line-clamp-none" : ""}`}>
        “{r.text}”
      </blockquote>
      {clamp && (
        <button type="button" onClick={() => setOpen(!open)} className="label-mono mt-3 self-start text-primary md:hidden" aria-expanded={open}>
          {open ? "Show less" : "Read full review"}
        </button>
      )}
      <footer className="mt-6 flex items-center justify-between border-t border-border pt-4">
        <p className="font-display text-2xl font-extrabold uppercase">{r.name}</p>
        <p className="label-mono text-steel">Google Review</p>
      </footer>
    </Reveal>
  );
}

export function GoogleReviewsButton({ label = "Read more reviews on Google" }: { label?: string }) {
  return (
    <a href={GBP} target="_blank" rel="noopener" className="inline-flex rounded-sm bg-primary px-8 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-primary-foreground transition-transform hover:-translate-y-0.5">
      {label}
    </a>
  );
}

/* ---------- Service area + hours ---------- */

export function MontanaMap() {
  return (
    <svg viewBox="0 0 400 220" className="mt-8 w-full max-w-lg" role="img" aria-label="Map of Montana with Great Falls marked">
      <path d="M10 10 L390 10 L390 210 L170 210 L160 190 L140 185 L130 160 L110 150 L105 125 L85 110 L80 85 L60 70 L45 40 L25 30 Z" fill="none" stroke="currentColor" strokeOpacity="0.5" strokeWidth="2" className="text-steel" />
      <circle cx="175" cy="75" r="40" className="fill-primary/10 stroke-primary/40" strokeDasharray="4 4" />
      <circle cx="175" cy="75" r="7" className="fill-primary" />
      <text x="188" y="70" className="fill-foreground font-display" fontSize="16" fontWeight="800">GREAT FALLS</text>
    </svg>
  );
}

export function HoursCard() {
  return (
    <Reveal className="rounded-sm border-4 border-primary bg-card p-8 text-center shadow-2xl">
      <p className="label-mono text-steel">— Shop hours —</p>
      <p className="mt-4 font-display text-6xl font-black uppercase leading-none text-primary">Open</p>
      <p className="font-display text-4xl font-black uppercase">7 Days a Week</p>
      <div className="my-6 h-px bg-border" />
      <p className="font-display text-2xl font-bold uppercase">Monday–Sunday</p>
      <p className="font-display text-3xl font-black">8:00 AM – 9:00 PM</p>
      <div className="mt-8 flex flex-wrap justify-center gap-3"><CallButton label="Call" /><TextButton label="Text" /></div>
    </Reveal>
  );
}

export function TownChips() {
  return (
    <ul className="mt-6 flex flex-wrap gap-2">
      {towns.map((t) => (
        <li key={t} className="flex items-center gap-1 rounded-sm border border-border bg-card px-3 py-1.5 font-display text-lg font-bold uppercase">
          <MapPin className="h-4 w-4 text-primary" aria-hidden />{t}
        </li>
      ))}
      <li className="rounded-sm border border-primary px-3 py-1.5 font-display text-lg font-bold uppercase text-primary">and statewide travel available</li>
    </ul>
  );
}

export function ServiceAreaHours() {
  return (
    <section className="topo cut-both bg-charcoal-deep py-32" aria-labelledby="area-h">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1.3fr_1fr] lg:items-center">
        <Reveal>
          <Eyebrow>Service area</Eyebrow>
          <h2 id="area-h" className="mt-4 text-5xl font-black md:text-6xl">Serving Great Falls & Across Montana</h2>
          <MontanaMap />
          <TownChips />
          <Link to="/service-area" className="label-mono mt-6 inline-block text-primary hover:underline">See the full service area →</Link>
        </Reveal>
        <HoursCard />
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */

export function FaqList({ items }: { items: Faq[] }) {
  return (
    <div className="mt-10 divide-y divide-border border-y border-border">
      {items.map((f) => (
        <details key={f.q} className="group py-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-2xl font-bold uppercase">
            {f.q}
            <ChevronDown className="h-6 w-6 shrink-0 text-primary transition-transform group-open:rotate-180" aria-hidden />
          </summary>
          <p className="mt-3 text-foreground/80">{f.a}</p>
        </details>
      ))}
    </div>
  );
}

export function faqLd(items: Faq[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
}

/* ---------- CTA ---------- */

export function FinalCta({ title, sub = "Open 7 days, 8AM–9PM" }: { title?: ReactNode; sub?: string }) {
  return (
    <section className="hazard p-2">
      <div className="topo bg-charcoal-deep px-4 py-20 text-center">
        <h2 className="mx-auto max-w-3xl text-5xl font-black md:text-7xl">
          {title ?? <>Broken down? <span className="text-primary">Don't wait on a shop.</span></>}
        </h2>
        <p className="label-mono mt-5 text-foreground/80">{sub}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <CallButton size="lg" />
          <TextButton label="Text Josh" size="lg" />
        </div>
      </div>
    </section>
  );
}
